<template>
  <v-container class="py-0" fluid>
    <v-row>
      <v-col>
        <v-container py-0 fluid>
          <v-row justify="end">
            <v-btn
              color="primary"
              @click="onLinkServicesClick"
              :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
            >
              <v-icon left>mdi-plus-circle</v-icon>Add/Link Services
            </v-btn>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <dates-range-picker
                  :gt-date-from-string="dateFromFilter"
                  :gt-date-to-string="dateToFilter"
                  :outlined="outlinedInputs"
                  :clearable="true"
                  :date-from-range-start="dateFromRangeStart"
                  :date-to-range-end="dateToRangeEnd"
                  @input="onDatesRangeSelected"
                  @onClear="onClearDates"
                  hide-details
                  dense
                ></dates-range-picker>
              </v-col>
              <v-col>
                <gt-multi-select
                  :items="serviceItems"
                  v-model="selectedServicesFilter"
                  item-text="name"
                  item-value="id"
                  label="Service"
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                ></gt-multi-select>
              </v-col>
              <v-col>
                <supplier-list-select-by-type
                  v-model="targetSupplierFilter"
                  supplier-type="ACCOMMODATION"
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                  clearable
                />
              </v-col>
              <v-col>
                <gt-multi-select
                  v-model="targetServicesFilter"
                  :items="targetServices"
                  :outlined="outlinedInputs"
                  hide-details
                  dense
                  label="Target Service"
                  :disabled="!targetSupplierFilter"
                  :loading="$apollo.loading"
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="notInUseFilter"
                  label="Not In Use"
                  dense
                  hide-details
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card outlined>
          <v-data-table
            :items="items"
            :headers="visibleHeaders"
            :loading="$apollo.loading"
            v-model="selected"
            show-select
            item-key="id"
            :items-per-page="-1"
            fixed-header
          >
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                @deleteItem="onDeleteItem"
                @editItem="onEditItem"
                hide-view
              >
                <template v-slot:extraButton>
                  <v-tooltip
                    bottom
                    v-if="!$store.getters.hasAccessTo('suppliers:view_only')"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        small
                        class="mx-0"
                        @click="cloneItem(item)"
                      >
                        <v-icon color="primary" small>mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>Copy</span>
                  </v-tooltip>
                </template>
              </default-list-actions>
            </template>
            <template v-slot:item.notInUse="{ item }">
              <v-simple-checkbox v-model="item.notInUse" disabled />
            </template>
            <template v-slot:item.isMandatory="{ item }">
              <v-simple-checkbox v-model="item.isMandatory" disabled />
            </template>
            <template v-slot:item.targetServiceMarketPlace="{ item }">
              <v-simple-checkbox
                v-model="item.targetServiceMarketPlace"
                disabled
              />
            </template>
            <template v-slot:item.dateFrom="{ item }">
              {{ item.dateFrom | formatDate }}
            </template>
            <template v-slot:item.dateTo="{ item }">
              {{ item.dateTo | formatDate }}
            </template>
            <template v-slot:item.targetSupplier="{ item }">
              <supplier-label
                v-if="item.targetSupplierId"
                v-model="item.targetSupplier"
                :supplier-type="item.targetSupplierServiceType"
                :supplier-id="item.targetSupplierId"
                :new-tab="true"
              ></supplier-label>
            </template>
            <template v-slot:footer>
              <list-actions
                v-show="selected.length > 0"
                :selected="selected"
                :columns="exportColumns"
                list="linkedServices"
                :filter="exportFilter"
                hide-unexport
              >
                <v-btn fab dark small color="indigo" @click="onShowCloneItems">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" small>mdi-content-copy</v-icon>
                    </template>
                    <span>Copy</span>
                  </v-tooltip>
                </v-btn>
              </list-actions>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <add-linked-services-dialog
      :show="showAddLinkedServicesDialog"
      :supplier="supplier"
      :services="services"
      @close="showAddLinkedServicesDialog = false"
      @create="onCreate"
      :key="addLinkedServicesRefreshKey"
    />
    <edit-linked-service-dialog
      :show="showEditLinkedServiceDialog"
      :value="selectedLinkedService"
      :services="services"
      @close="showEditLinkedServiceDialog = false"
      @save="onSaveItem"
    />
    <clone-linked-services
      :show="showCloneLinkedServices"
      :services="services"
      @save="onCloneItems"
      @close="showCloneLinkedServices = false"
    />
    <v-row v-if="debug">
      <v-col>
        <pre>{{ targetServicesFilter }}</pre>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import AddLinkedServicesDialog from "./AddLinkedServicesDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
import DefaultListActions from "../../shared/DefaultListActions.vue";
import EditLinkedServiceDialog from "./EditLinkedServiceDialog.vue";
import GtMultiSelect from "../../shared/GtMultiSelect.vue";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";
import CloneLinkedServices from "./CloneLinkedServices.vue";
import ListActions from "../../shared/list-actions/ListActions.vue";
import SupplierListSelectByType from "../../shared/SupplierListSelectByType.vue";
import SupplierLabel from "../../shared/labels/SupplierLabel.vue";
const moment = require("moment");
const randomize = require("randomatic");
const CREATE_LINKED_SERVICES = gql`
  mutation createLinkedServices($input: [LinkedServiceInput!]!) {
    createLinkedServices(input: $input) {
      id
    }
  }
`;
const UPDATE_LINKED_SERVICE = gql`
  mutation updateLinkedService($input: LinkedServiceInput!) {
    updateLinkedService(input: $input) {
      id
      notInUse
      isMandatory
      dateFrom
      dateTo
    }
  }
`;
const LINKED_SERVICES = gql`
  query linkedServices($filter: LinkedServiceFilterType) {
    linkedServices(filter: $filter) {
      edges {
        node {
          id
          groupId
          sourceService {
            id
            name
            supplier {
              id
              name
            }
          }
          targetService {
            id
            name
            marketPlace
            supplier {
              id
              name
              serviceType
            }
          }
          dateFrom
          dateTo
          notInUse
          isMandatory
          agentGroup {
            id
            name
          }
        }
      }
    }
  }
`;
const DELETE_LINKED_SERVICE = gql`
  mutation deleteLinkedService($id: ID!) {
    deleteLinkedService(id: $id) {
      id
    }
  }
`;
const CLONE_LINKED_SERVICES = gql`
  mutation cloneLinkedServices($input: CloneLinkedServicesInput!) {
    cloneLinkedServices(input: $input) {
      id
    }
  }
`;
const TARGET_SUPPLIER_SERVICES = gql`
  query targetSupplierServices($id: ID!) {
    targetSupplier: supplier(id: $id) {
      id
      services(filter: { not_in_use_eq: false }) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;
export default {
  components: {
    AddLinkedServicesDialog,
    DefaultListActions,
    EditLinkedServiceDialog,
    GtMultiSelect,
    DatesRangePicker,
    CloneLinkedServices,
    ListActions,
    SupplierListSelectByType,
    SupplierLabel,
  },
  name: "linked-services",

  props: {
    supplier: {
      type: Object,
      default: () => null,
    },
    services: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      debug: false,
      addLinkedServicesRefreshKey: randomize("Aa0", 32),
      showAddLinkedServicesDialog: false,
      showEditLinkedServiceDialog: false,
      showCloneLinkedServices: false,
      selectedLinkedService: null,
      selected: [],
      hideDelete: true,
      targetSupplierFilter: null,
      targetServicesFilter: [],
      headers: [
        {
          text: "Source Service",
          name: "service",
          value: "sourceService",
        },
        {
          text: "Target Supplier",
          name: "targetSupplier",
          value: "targetSupplier",
        },
        {
          text: "Target Service",
          name: "targetService",
          value: "targetService",
        },
        {
          text: "Target Service Market Place",
          name: "targetServiceMarketPlace",
          value: "targetServiceMarketPlace",
        },
        {
          text: "Agent/Group",
          name: "agentGroupName",
          value: "agentGroupName",
        },

        {
          text: "Date From",
          name: "dateFrom",
          value: "dateFrom",
        },
        {
          text: "Date To",
          name: "dateTo",
          value: "dateTo",
        },
        {
          text: "Not In Use",
          name: "notInUse",
          value: "notInUse",
        },
        {
          text: "Is Mandatory",
          name: "isMandatory",
          value: "isMandatory",
        },
        {
          text: "Actions",
          name: "actions",
          value: "actions",
        },
      ],
      dateFromFilter: moment().toISOString(true),
      dateToFilter: "",
      selectedServicesFilter: [],
      notInUseFilter: false,
    };
  },

  computed: {
    visibleHeaders() {
      return this.headers.filter((h) => {
        if (h.name === "targetServiceMarketPlace") {
          return this.$store.getters.hasAccessTo("third_party:marketplace");
        }
        return true;
      });
    },
    targetServices() {
      return this.targetSupplier
        ? this.targetSupplier.services.edges.map(removeGQLTypename)
        : [];
    },
    items() {
      return this.linkedServices && this.linkedServices.edges
        ? this.linkedServices.edges.map(removeGQLTypename).map((ls) => ({
            id: ls.id,
            sourceService: ls.sourceService.name,
            sourceServiceId: ls.sourceService.id,
            targetSupplier: ls.targetService.supplier.name,
            targetSupplierId: ls.targetService.supplier.id,
            targetSupplierServiceType: ls.targetService.supplier.serviceType,
            targetServiceId: ls.targetService.id,
            targetService: ls.targetService.name,
            targetServiceMarketPlace: ls.targetService.marketPlace,
            agentGroupName: ls.agentGroup ? ls.agentGroup.name : "All Groups",
            groupId: ls.agentGroup ? ls.agentGroup.id : null,
            dateFrom: ls.dateFrom,
            dateTo: ls.dateTo,
            notInUse: ls.notInUse,
            isMandatory: ls.isMandatory,
          }))
        : [];
    },
    dateFromRangeStart() {
      return moment().subtract(10, "years").toISOString(true);
    },
    dateToRangeEnd() {
      return moment().add(10, "years").toISOString(true);
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    serviceItems() {
      return this.services
        .map(removeGQLTypename)
        .filter((s) => !s.notInUse);
    },
    exportColumns() {
      return this.headers
        .filter((h) => h.name !== "actions")
        .map((h) => h.name);
    },
    exportFilter() {
      return {
        supplierId_eq: this.supplier.id,
        notInUse_eq: this.notInUseFilter,
        dateFrom_lte:
          this.dateToFilter.toString() != "" ? this.dateToFilter : undefined,
        dateTo_gte:
          this.dateFromFilter.toString() != ""
            ? this.dateFromFilter
            : undefined,
        sourceServices_ids_in:
          this.selectedServicesFilter.length > 0
            ? this.selectedServicesFilter
            : undefined,
      };
    },
  },

  methods: {
    cloneItem(item) {
      console.log("clone item", item);
      this.selected.push(item);
      this.showCloneLinkedServices = true;
    },
    onShowCloneItems() {
      console.log("clone items");
      this.showCloneLinkedServices = true;
    },
    onCloneItems(serviceIds) {
      console.log("clone items", serviceIds);
      this.showCloneLinkedServices = false;
      this.$apollo
        .mutate({
          mutation: CLONE_LINKED_SERVICES,
          variables: {
            input: {
              linkedServices: this.selected.map((ls) => ls.id),
              targetIds: serviceIds,
            },
          },
          refetchQueries: [
            {
              query: LINKED_SERVICES,
              variables: {
                filter: {
                  supplierId_eq: this.supplier.id,
                  notInUse_eq: this.notInUseFilter,
                  dateFrom_lte:
                    this.dateToFilter.toString() != ""
                      ? this.dateToFilter
                      : undefined,
                  dateTo_gte:
                    this.dateFromFilter.toString() != ""
                      ? this.dateFromFilter
                      : undefined,
                  sourceServices_ids_in:
                    this.selectedServicesFilter.length > 0
                      ? this.selectedServicesFilter
                      : undefined,
                },
              },
            },
          ],
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Linked services cloned successfully",
            color: "success",
          });
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onSaveItem(item) {
      console.log("onSaveItem", item);
      this.showEditLinkedServiceDialog = false;
      this.$apollo
        .mutate({
          mutation: UPDATE_LINKED_SERVICE,
          variables: { input: item },
          refetchQueries: [
            {
              query: LINKED_SERVICES,
              variables: { filter: { supplierId_eq: this.supplier.id } },
            },
          ],
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Linked service updated successfully",
            color: "success",
          });
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onEditItem(item) {
      console.log("onEditItem", item);
      this.selectedLinkedService = { ...item };
      this.showEditLinkedServiceDialog = true;
    },
    onLinkServicesClick() {
      this.addLinkedServicesRefreshKey = randomize("Aa0", 32);
      this.showAddLinkedServicesDialog = true;
    },
    onCreate(linkedServices) {
      this.$apollo
        .mutate({
          mutation: CREATE_LINKED_SERVICES,
          variables: { input: linkedServices },
          refetchQueries: [
            {
              query: LINKED_SERVICES,
              variables: {
                filter: {
                  supplierId_eq: this.supplier.id,
                  notInUse_eq: this.notInUseFilter,
                  dateFrom_lte:
                    this.dateToFilter.toString() != ""
                      ? this.dateToFilter
                      : undefined,
                  dateTo_gte:
                    this.dateFromFilter.toString() != ""
                      ? this.dateFromFilter
                      : undefined,
                  sourceServices_ids_in:
                    this.selectedServicesFilter.length > 0
                      ? this.selectedServicesFilter
                      : undefined,
                },
              },
            },
          ],
        })
        .then((result) => {
          this.showAddLinkedServicesDialog = false;
          this.$store.dispatch("showAppSnackbar", {
            msg: "Linked services created successfully",
            color: "success",
          });
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onDeleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DELETE_LINKED_SERVICE,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: LINKED_SERVICES,
              variables: {
                filter: {
                  supplierId_eq: this.supplier.id,
                  notInUse_eq: this.notInUseFilter,
                  dateFrom_lte:
                    this.dateToFilter.toString() != ""
                      ? this.dateToFilter
                      : undefined,
                  dateTo_gte:
                    this.dateFromFilter.toString() != ""
                      ? this.dateFromFilter
                      : undefined,
                  sourceServices_ids_in:
                    this.selectedServicesFilter.length > 0
                      ? this.selectedServicesFilter
                      : undefined,
                },
              },
            },
          ],
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Linked service deleted successfully",
            color: "success",
          });
        })
        .catch((error) => {
          const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
          console.log(errorMsg);
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onDatesRangeSelected(event) {
      this.dateFromFilter = event.dateFrom;
      this.dateToFilter = event.dateTo;
    },
    onClearDates() {
      this.dateFromFilter = "";
      this.dateToFilter = "";
    },
  },
  apollo: {
    targetSupplier: {
      query: TARGET_SUPPLIER_SERVICES,
      variables() {
        return { id: this.targetSupplierFilter };
      },
      skip() {
        return !this.targetSupplierFilter;
      },
    },
    linkedServices: {
      query: LINKED_SERVICES,
      variables() {
        return {
          filter: {
            supplierId_eq: this.supplier.id,
            notInUse_eq: this.notInUseFilter,
            dateFrom_lte:
              this.dateToFilter.toString() != ""
                ? this.dateToFilter
                : undefined,
            dateTo_gte:
              this.dateFromFilter.toString() != ""
                ? this.dateFromFilter
                : undefined,
            sourceServices_ids_in:
              this.selectedServicesFilter.length > 0
                ? this.selectedServicesFilter
                : undefined,
            targetServices_ids_in:
              this.targetServicesFilter.length > 0
                ? this.targetServicesFilter
                : undefined,
          },
        };
      },
    },
  },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/gt/suppliers/linked-services/LinkedServices.vue